import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/ui';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { CloudDownload } from '@/components/ui/icons/Icons';
import { useI18n } from '@/context/I18nProvider';
import { ApiClient } from '@/services/api-client';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
type ExportSingleConversationButtonProps = {
  leadId: string;
  leadName: string;
};

// ── Module-level queue system ──────────────────────────────────────
type QueueItem = {
  leadId: string;
  jobId: string;
  leadName: string;
};
const exportQueue: QueueItem[] = [];
const pendingLeads = new Set<string>(); // leads with jobs in queue or processing
let isProcessing = false;

// Listeners: mounted components subscribe to know when their leadId finishes
const listeners = new Map<string, () => void>();
function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
async function processQueue() {
  if (isProcessing) return;
  if (exportQueue.length === 0) return;
  isProcessing = true;
  const item = exportQueue[0];

  // Poll until completed or failed
  const poll = () => new Promise<'completed' | 'failed'>(resolve => {
    const interval = setInterval(async () => {
      try {
        const status = await ApiClient.lead.getExportStatus({
          jobId: item.jobId
        });
        if (status.data.Status === 'completed') {
          clearInterval(interval);
          resolve('completed');
        } else if (status.data.Status === 'failed') {
          clearInterval(interval);
          resolve('failed');
        }
      } catch {
        // Transient failure — keep polling
      }
    }, 1000);
  });
  const result = await poll();
  if (result === 'completed') {
    try {
      const blob = await ApiClient.lead.downloadExport({
        jobId: item.jobId
      });
      const sanitizedName = (item.leadName || 'export').replace(/[^a-zA-Z0-9_-]/g, '_');
      triggerDownload(blob, `conversation-${sanitizedName}.csv`);
    } catch {
      // Download failed — toast will be shown via listener
    }
  }

  // Remove from queue and pending set
  exportQueue.shift();
  pendingLeads.delete(item.leadId);

  // Notify mounted component
  listeners.get(item.leadId)?.();
  isProcessing = false;

  // Process next item
  processQueue();
}
function enqueueExport(item: QueueItem) {
  exportQueue.push(item);
  pendingLeads.add(item.leadId);
  processQueue();
}
// ── End queue system ───────────────────────────────────────────────

const ExportSingleConversationButton = t0 => {
  const $ = _c(26);
  const {
    leadId,
    leadName
  } = t0;
  const {
    t
  } = useI18n();
  let t1;
  if ($[0] !== leadId) {
    t1 = () => pendingLeads.has(leadId);
    $[0] = leadId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [loading, setLoading] = useState(t1);
  let t2;
  let t3;
  if ($[2] !== leadId) {
    t2 = () => {
      listeners.set(leadId, () => setLoading(false));
      return () => {
        listeners.delete(leadId);
      };
    };
    t3 = [leadId];
    $[2] = leadId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[5] !== leadId) {
    t4 = () => {
      setLoading(pendingLeads.has(leadId));
    };
    t5 = [leadId];
    $[5] = leadId;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== leadId || $[9] !== leadName || $[10] !== t) {
    t6 = async () => {
      if (pendingLeads.has(leadId)) {
        return;
      }
      setLoading(true);
      try {
        const res = await ApiClient.lead.startExportConversations({
          leadId
        });
        enqueueExport({
          leadId,
          jobId: res.data.JobID,
          leadName
        });
      } catch {
        setLoading(false);
        toast.error(t("conversations.export.error"));
      }
    };
    $[8] = leadId;
    $[9] = leadName;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleExport = t6;
  const t7 = `h-7 gap-1.5 px-2.5 py-2 shadow-none relative ${loading ? "animate-pulse !text-blue-500 !border-blue-300 !opacity-100 !bg-white" : "hover:text-blue-500 hover:border-blue-300"}`;
  let t8;
  if ($[12] !== loading) {
    t8 = loading ? <CloudDownload className="size-4 animate-bounce" /> : <CloudDownload className="size-4" />;
    $[12] = loading;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== handleExport || $[15] !== loading || $[16] !== t7 || $[17] !== t8) {
    t9 = <TooltipTrigger asChild={true}><Button variant="outline" size="sm" className={t7} disabled={loading} onClick={handleExport}>{t8}</Button></TooltipTrigger>;
    $[14] = handleExport;
    $[15] = loading;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("conversations.export.exportSingle");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <TooltipContent><p>{t10}</p></TooltipContent>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <TooltipProvider><Tooltip>{t9}{t11}</Tooltip></TooltipProvider>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
export { ExportSingleConversationButton };